import {
  lsRemoveItem,
  lsGetItem,
  lsSetItem,
  localStorageWorks
} from "../../LocalStorageWrapper";
import jwtDecode from "jwt-decode";
import { DocuclipperToken } from "../../docuclipper/DocuclipperTypes";
import * as Sentry from "@sentry/browser";
import { clearSentryContext } from "../../utils/sentryUtils";

// ---------------------------------------------------------------------------
// Sandbox token lives in sessionStorage so it is tab-isolated and never
// overwrites the real token in localStorage.
// ---------------------------------------------------------------------------
const SANDBOX_JWT_KEY = "docuclipper.sandbox-jwt";

export const getSandboxTokenStr = (): string | null => {
  try {
    return sessionStorage.getItem(SANDBOX_JWT_KEY);
  } catch {
    return null;
  }
};

const setSandboxTokenStr = (tokenStr: string) => {
  try {
    sessionStorage.setItem(SANDBOX_JWT_KEY, tokenStr);
  } catch {}
};

const removeSandboxTokenStr = () => {
  try {
    sessionStorage.removeItem(SANDBOX_JWT_KEY);
  } catch {}
};

// ---------------------------------------------------------------------------
// Bootstrap: prefer sandbox token (tab-local) over real token
// ---------------------------------------------------------------------------
const sandboxTokenStrInit = getSandboxTokenStr();
const realTokenStr = lsGetItem("docuclipper.jwt");

const activeTokenStr = sandboxTokenStrInit || realTokenStr;
let token: DocuclipperToken | null = null;

if (activeTokenStr) {
  try {
    token = jwtDecode(activeTokenStr);
  } catch (_err) {
    token = null;
    if (sandboxTokenStrInit) {
      removeSandboxTokenStr();
    } else {
      lsRemoveItem("docuclipper.jwt");
    }
  }
}

const initialState: {
  token: DocuclipperToken | null;
  tokenStr: string | null;
  isSandboxMode: boolean;
} = {
  token,
  tokenStr: activeTokenStr,
  isSandboxMode: !!sandboxTokenStrInit
};

export const SET_TOKEN = "set-token";
export const DELETE_TOKEN = "delete-token";
export const SET_SANDBOX_TOKEN = "set-sandbox-token";
export const EXIT_SANDBOX = "exit-sandbox";

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_TOKEN:
      return {
        ...state,
        token: action.payload.token,
        tokenStr: action.payload.tokenStr,
        isSandboxMode: false
      };
    case DELETE_TOKEN:
      clearSentryContext();
      return {
        ...state,
        token: null,
        tokenStr: null,
        isSandboxMode: false
      };
    case SET_SANDBOX_TOKEN:
      return {
        ...state,
        token: action.payload.token,
        tokenStr: action.payload.tokenStr,
        isSandboxMode: true
      };
    case EXIT_SANDBOX: {
      // Restore real token from localStorage
      const realStr = lsGetItem("docuclipper.jwt");
      let realToken: DocuclipperToken | null = null;
      if (realStr) {
        try { realToken = jwtDecode(realStr); } catch {}
      }
      return {
        ...state,
        token: realToken,
        tokenStr: realStr,
        isSandboxMode: false
      };
    }
    default:
      return state;
  }
}

export type SetTokenFn = (tokenStr: string) => any;
export const setToken = (tokenStr: string) => {
  let token;
  try {
    token = jwtDecode(tokenStr);
  } catch (err) {
    Sentry.captureException(err, {
      extra: { tokenStr }
    });
    throw err;
  }

  withLocalStorageCheck(() => lsSetItem("docuclipper.jwt", tokenStr));

  return {
    type: SET_TOKEN,
    payload: { token, tokenStr }
  };
};

export const setSandboxToken = (tokenStr: string) => {
  let token: DocuclipperToken;
  try {
    token = jwtDecode(tokenStr);
  } catch (err) {
    throw err;
  }
  setSandboxTokenStr(tokenStr);
  return {
    type: SET_SANDBOX_TOKEN,
    payload: { token, tokenStr }
  };
};

export const exitSandbox = () => {
  removeSandboxTokenStr();
  return { type: EXIT_SANDBOX, payload: {} };
};

export type DeleteTokenFn = () => any;
export const deleteToken = () => {
  withLocalStorageCheck(() => lsRemoveItem("docuclipper.jwt"));
  removeSandboxTokenStr();

  return {
    type: DELETE_TOKEN,
    payload: {}
  };
};

const withLocalStorageCheck = (fn: () => any) => {
  if (!localStorageWorks()) {
    // store.dispatch({
    //   type: CREATE_ALERT,
    //   payload: {
    //     alert: {
    //       type: "error",
    //       timeout: 0,
    //       message:
    //         "Cookies are not enabled on your browser. Please enable cookies in your browser preferences to continue."
    //     }
    //   }
    // });
    return null;
  } else {
    return fn();
  }
};
